import { orgQueryKeys, userQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import { useAppStore } from '@/stores/app';
import { PaginatedUsersResponse, Role, UserPublic } from '@/types';
import { isUnauthorizedError } from '@/types/errors';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_SEARCH_LIMIT, SEARCH_CACHE_TIME, TRANSLATION_KEYS, USER_SEARCH_MIN_LENGTH } from './constants';
import type { AddUserParams, OrgUser, UserSearchResult } from './types';
import { isValidUserSearchResults } from './validation';

// Every cache listing this org's users sits under its own key root, and the Users page's global grid
// caches for a day, so missing one leaves that view showing stale membership (POR-501).
export function invalidateOrgUserQueries(queryClient: QueryClient, orgId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: orgQueryKeys.users(orgId) }),
    queryClient.invalidateQueries({ queryKey: userQueryKeys.orgUsers(orgId) }),
    queryClient.invalidateQueries({ queryKey: userQueryKeys.orgUserOptions(orgId) }),
    // The ['users'] root: User Details reads a shared all-users list under userQueryKeys.list(),
    // and a stale miss there renders as a definitive "User not found."
    queryClient.invalidateQueries({ queryKey: userQueryKeys.all }),
    queryClient.invalidateQueries({ queryKey: agGridDatasourceQueryKey(userQueryKeys.endpoints.globalSearch()) }),
  ]);
}

/** Fetches the org's users, flattening `allowed_roles[orgId]` into the `roles` the modal renders. */
export function useOrgUsers(orgId: string) {
  return useQuery<OrgUser[]>({
    queryKey: orgQueryKeys.users(orgId),
    queryFn: async () => {
      const response = await apiClient.get<UserPublic[]>(`/orgs/${orgId}/users`);
      return (response ?? []).map(user => ({
        ...user,
        roles: user.allowed_roles?.[orgId] ?? [],
      }));
    },
    retry: (failureCount, error) => {
      // Don't retry on unauthorized errors
      if (isUnauthorizedError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 0,
    gcTime: 0, // Discard immediately — transient user list, not shared across navigations
  });
}

/** Searches the sidebar-selected org's users, runtime-validating the response and surfacing failures as query errors. */
export function useUserSearch(searchQuery: string, t: TFunction) {
  return useQuery<UserSearchResult[]>({
    queryKey: orgQueryKeys.userSearch(searchQuery),
    queryFn: async () => {
      const orgId = useAppStore.getState().selectedOrganization?.id;
      if (!searchQuery || searchQuery.length < USER_SEARCH_MIN_LENGTH) {
        return [];
      }

      // This endpoint now returns a PaginatedUsersResponse envelope rather than a bare array.
      // The generic matters: without it `apiClient.post` returns `unknown`, so the envelope
      // change type-checked clean and only failed at runtime in the validator below.
      const envelope = await apiClient.post<PaginatedUsersResponse>(`/orgs/${orgId}/users/search`, {
        q: searchQuery,
        limit: DEFAULT_SEARCH_LIMIT,
      });
      // Deliberately `unknown`: the runtime guard below is the real check, and typing this as
      // UserPublic[] would narrow the failure branch to `never` and make the diagnostics dead.
      const response: unknown = envelope?.records ?? [];

      // Validate response structure at runtime
      if (!isValidUserSearchResults(response)) {
        const errorMsg = t(TRANSLATION_KEYS.USER_SEARCH_INVALID_RESPONSE);

        // Build safe debug info without exposing sensitive data
        const debugInfo = {
          responseType: typeof response,
          isArray: Array.isArray(response),
          length: Array.isArray(response) ? response.length : undefined,
          hasItems: Array.isArray(response) && response.length > 0,
          firstItemKeys: Array.isArray(response) && response.length > 0 ? Object.keys(response[0] || {}) : undefined,
        };

        console.error('Invalid response format from user search API', debugInfo);

        // Only include structural information in development to avoid exposing sensitive data
        const isDevelopment = import.meta.env.DEV;
        const detailedError = isDevelopment
          ? ` - Expected array of UserPublic objects with id, email, full_name. Received ${debugInfo.responseType}${debugInfo.isArray ? ` array with ${debugInfo.length} items` : ''}${debugInfo.firstItemKeys ? ` with keys: ${debugInfo.firstItemKeys.join(', ')}` : ''}.`
          : ' - Expected array of UserPublic objects with id, email, full_name.';

        throw new Error(`${errorMsg}${detailedError}`);
      }

      return response;
    },
    enabled: searchQuery.length >= USER_SEARCH_MIN_LENGTH,
    staleTime: SEARCH_CACHE_TIME,
    retry: 1,
  });
}

/**
 * Hook to add a user to an organization
 */
export function useAddUser(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: AddUserParams) => {
      const url = `/orgs/${orgId}/users`;
      const body = { email, roles };

      return await apiClient.post(url, body);
    },
    onSuccess: () => invalidateOrgUserQueries(queryClient, orgId),
  });
}

/**
 * Hook to remove a user from an organization
 */
export interface RemoveUserParams {
  email: string;
  roles?: Role[] | null;
}

export function useRemoveUser(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: RemoveUserParams) => {
      return await apiClient.delete(`/orgs/${orgId}/users`, { email, roles });
    },
    onSuccess: () => invalidateOrgUserQueries(queryClient, orgId),
  });
}

/**
 * Hook that provides the remove user handler with toast notifications
 */
export function useRemoveUserHandler(orgId: string, orgName: string, t: TFunction) {
  const removeUserMutation = useRemoveUser(orgId);

  return useCallback(
    ({ email, userName, roles }: { email: string; userName: string; roles: Role[] }) => {
      const removePromise = removeUserMutation.mutateAsync({ email, roles });

      toast.promise(removePromise, {
        loading: t(TRANSLATION_KEYS.REMOVING),
        success: () => t(TRANSLATION_KEYS.USER_REMOVED_SUCCESS, { name: userName, orgName }),
        error: (error: Error) => error.message || t(TRANSLATION_KEYS.USER_REMOVED_FAILED, { orgName }),
      });
    },
    [removeUserMutation, t, orgName],
  );
}

/**
 * Hook to update a user's roles in an organization
 */
export function useUpdateUserRoles(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: { email: string; roles: Role[] }) => {
      return await apiClient.post(`/orgs/${orgId}/users`, { email, roles });
    },
    onSuccess: () => invalidateOrgUserQueries(queryClient, orgId),
  });
}
